import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, NgZone, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Localization} from '../../../projects/common/src/lib/interface/localization.extension';

/** One measurement on the world image, in the image's own pixels. */
interface SceneShape {
  id: string;
  /** An area (closed polygon, a: and p:) or a distance (open line, d:). */
  kind: 'area' | 'line';
  nameKey: string;
  /** SVG `points`, drawn from the first point on. */
  points: string;
  /** Each vertex with the moment (0..1 of the outline) the line reaches it. */
  vertices: {x: number, y: number, at: number}[];
  /** Where the measurement label is centred. */
  label: {x: number, y: number};
  areaKm2: number;
  /** Perimeter of an area, or the length of a line. */
  lengthKm: number;
}

/** How long the outline takes to draw; the vertices pop in along the way. */
const DRAW_MS = 2600;
/** From the outline closing to it fading away. */
const HOLD_MS = 5200;
const FADE_MS = 700;
/** Lets the page's entrance finish before the first outline starts. */
const FIRST_DELAY_MS = 900;
const COUNT_MS = 1500;

function buildShape(id: string, kind: 'area' | 'line', nameKey: string, pts: [number, number][],
                    label: [number, number], areaKm2: number, lengthKm: number): SceneShape {
  // A polygon's outline runs back to its first point; a line's does not.
  const segments = kind === 'area' ? pts.length : pts.length - 1;
  const starts: number[] = [];
  let total = 0;
  for (let i = 0; i < pts.length; i++) {
    starts.push(total);
    if (i < segments) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      total += Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
  }
  return {
    id,
    kind,
    nameKey,
    points: pts.map(p => p.join(',')).join(' '),
    vertices: pts.map((p, i) => ({x: p[0], y: p[1], at: starts[i] / total})),
    label: {x: label[0], y: label[1]},
    areaKm2,
    lengthKm,
  };
}

/**
 * The story side of the sign-in page: the world from above, being measured.
 *
 * A satellite picture of the world drifts slowly, and a polygon is drawn round
 * a continent vertex by vertex, then closes and shows its area and perimeter
 * in the same dark label the map uses (a: in red, p: in green); then a line
 * measures a distance across the ocean. It says what the product does before
 * a word is read.
 *
 * Everything is one SVG in the image's own coordinates, so the outlines stay
 * on the coastlines however the picture is cropped or scaled. The sequence
 * runs outside Angular (class toggles and a text counter), so it never costs
 * a change-detection pass, and it pauses while the tab is hidden.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-login-scene',
  templateUrl: 'login-scene.component.html',
  styleUrls: ['login-scene.component.scss'],
  // The CSS draw and the vertex timings share one duration.
  host: {'[style.--scene-draw]': 'drawMs + "ms"'},
})
export class LoginSceneComponent extends Localization implements AfterViewInit {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  readonly drawMs = DRAW_MS;

  // Traced on /assets/images/background/world_map.jpg (1200 x 800). Africa's
  // area is the continent's own, its perimeter that of this simplified
  // outline; Dakar to Natal (the narrowest crossing of the Atlantic) is the
  // great-circle distance between the two cities.
  readonly shapes: SceneShape[] = [
    buildShape('africa', 'area', 'login_scene_africa', [
      [612, 267], [650, 260], [715, 261], [725, 275], [748, 283], [780, 276], [800, 283], [843, 288],
      [870, 333], [900, 385], [920, 398], [949, 396], [930, 435], [905, 470], [890, 500], [880, 540],
      [865, 580], [840, 620], [800, 654], [768, 654], [748, 610], [733, 555], [735, 515], [720, 475],
      [710, 443], [685, 433], [645, 436], [605, 420], [580, 390], [565, 343], [575, 320], [595, 295],
    ], [802, 470], 30370000, 21400),
    buildShape('atlantic', 'line', 'login_scene_atlantic', [
      [565, 343], [463, 503],
    ], [430, 392], 0, 3004),
  ];

  private timers: any[] = [];
  private frame = 0;
  private running = false;
  private next = 0;

  constructor() {
    super();
    // Numbers follow the page language (30.370.000 in German, 30,370,000 in English).
    this.localization.getObservable().pipe(takeUntilDestroyed()).subscribe(() => this.writeFinalValues());
  }

  ngAfterViewInit(): void {
    this.writeFinalValues();
    // Under reduced motion the drawing still runs: a line being traced and a
    // number counting up are neither travel nor zoom (see _motion.scss). The
    // drift and the zooms are what the stylesheet takes away.
    // Only where the story side is on screen: on a phone the picture is a
    // backdrop behind the form and the outlines are hidden.
    const wide = window.matchMedia('(min-width: 1024px)');
    const sync = () => (wide.matches && !document.hidden) ? this.start() : this.stop();
    const onVisibility = () => sync();
    wide.addEventListener('change', sync);
    document.addEventListener('visibilitychange', onVisibility);
    this.destroyRef.onDestroy(() => {
      wide.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', onVisibility);
      this.stop();
    });
    sync();
  }

  private start() {
    if (this.running) {
      return;
    }
    this.running = true;
    this.zone.runOutsideAngular(() => this.later(FIRST_DELAY_MS, () => this.play(this.next)));
  }

  private stop() {
    this.running = false;
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
    cancelAnimationFrame(this.frame);
    this.groups().forEach(g => g.classList.remove('is-drawing', 'is-closed', 'is-leaving'));
    this.writeFinalValues();
  }

  private play(index: number) {
    const group = this.groups()[index];
    const shape = this.shapes[index];
    if (!group || !shape || !this.running) {
      return;
    }
    this.next = (index + 1) % this.shapes.length;
    this.writeValues(group, shape, 0);
    group.classList.remove('is-leaving', 'is-closed');
    // Restart the CSS animations even when the same shape plays twice in a row.
    void group.getBoundingClientRect();
    group.classList.add('is-drawing');
    this.later(DRAW_MS, () => {
      group.classList.add('is-closed');
      this.countUp(group, shape);
    });
    this.later(DRAW_MS + HOLD_MS, () => group.classList.add('is-leaving'));
    this.later(DRAW_MS + HOLD_MS + FADE_MS, () => {
      group.classList.remove('is-drawing', 'is-closed', 'is-leaving');
      this.play(this.next);
    });
  }

  private countUp(group: Element, shape: SceneShape) {
    const started = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / COUNT_MS);
      // Ease out: fast at first, settling on the value like a measurement does.
      this.writeValues(group, shape, 1 - Math.pow(1 - t, 3));
      if (t < 1 && this.running) {
        this.frame = requestAnimationFrame(tick);
      }
    };
    this.frame = requestAnimationFrame(tick);
  }

  private writeFinalValues() {
    this.groups().forEach((g, i) => this.writeValues(g, this.shapes[i], 1));
  }

  private writeValues(group: Element, shape: SceneShape, progress: number) {
    if (!group || !shape) {
      return;
    }
    const format = new Intl.NumberFormat(this.locale, {maximumFractionDigits: 0});
    const area = group.querySelector('[data-count="area"]');
    const perimeter = group.querySelector('[data-count="perimeter"]');
    if (area) {
      area.textContent = format.format(Math.round(shape.areaKm2 * progress));
    }
    if (perimeter) {
      perimeter.textContent = format.format(Math.round(shape.lengthKm * progress));
    }
  }

  private get locale(): string {
    const lang = (this.language || 'en').toString();
    // The strings use the legacy code for Indonesian.
    return lang === 'in' ? 'id' : lang;
  }

  private groups(): Element[] {
    return Array.from(this.host.nativeElement.querySelectorAll('.shape'));
  }

  private later(ms: number, fn: () => void) {
    this.timers.push(setTimeout(fn, ms));
  }
}
